// Summary: Pure buffer/validation helpers for the general-item ("פריט כללי") numpad modal. Quantity is a
// 1-2 digit integer (max 99); price is entered on the one-decimal money grid (NUMERIC(12,1) backend).
// Details: md/GENERAL_ITEM.md (dedicated feature doc - Frontend section).

export type GeneralItemField = 'quantity' | 'price';

export const MAX_GENERAL_ITEM_QUANTITY = 99;
export const GENERAL_ITEM_TEXT_MAX = 120;
export const GENERAL_ITEM_PRICE_MAX_INT_DIGITS = 5;
export const GENERAL_ITEM_PRICE_MAX = 99999;
export const GENERAL_ITEM_DEFAULT_QUANTITY = '';
export const GENERAL_ITEM_DEFAULT_PRICE = '';

// Parse a numeric buffer to a number; an empty or lone-dot buffer reads as 0.
export const parseGeneralItemNumber = (buffer: string): number => {
  if (!buffer || buffer === '.') {
    return 0;
  }
  const value = Number(buffer);
  return Number.isFinite(value) ? value : 0;
};

// Append a digit to the integer quantity buffer. Caps at 2 digits (=> max 99); strips a leading zero.
export const appendQuantityDigit = (buffer: string, digit: string): string => {
  const next = `${buffer}${digit}`.replace(/^0(\d)/, '$1');
  if (next.length > 2) {
    return buffer;
  }
  return next;
};

// Append a digit to the price buffer. Allows at most one fractional digit (one-decimal money grid).
export const appendPriceDigit = (buffer: string, digit: string): string => {
  if (buffer.includes('.')) {
    const fraction = buffer.split('.')[1] ?? '';
    if (fraction.length >= 1) {
      return buffer;
    }
    return `${buffer}${digit}`;
  }
  const next = `${buffer}${digit}`.replace(/^0(\d)/, '$1');
  if (next.length > GENERAL_ITEM_PRICE_MAX_INT_DIGITS) {
    return buffer;
  }
  return next;
};

// Add a decimal point to the price buffer (no-op if one already exists).
export const appendPriceDecimal = (buffer: string): string => {
  if (buffer.includes('.')) {
    return buffer;
  }
  return buffer.length === 0 ? '0.' : `${buffer}.`;
};

// Remove the last character from a buffer.
export const backspaceBuffer = (buffer: string): string =>
  buffer.length > 0 ? buffer.slice(0, -1) : '';

export const isQuantityValid = (quantityBuffer: string): boolean => {
  const quantity = parseGeneralItemNumber(quantityBuffer);
  return Number.isInteger(quantity) && quantity >= 1 && quantity <= MAX_GENERAL_ITEM_QUANTITY;
};

export const isPriceValid = (priceBuffer: string): boolean => {
  if (!/^\d+(?:\.\d?)?$/.test(priceBuffer)) return false;
  const price = parseGeneralItemNumber(priceBuffer);
  return price >= 0 && price <= GENERAL_ITEM_PRICE_MAX;
};

export const isGeneralItemValid = (quantityBuffer: string, priceBuffer: string): boolean =>
  isQuantityValid(quantityBuffer) && isPriceValid(priceBuffer);

// Line total preview (qty × price) on the one-decimal money grid, matching the backend rounding.
export const computeGeneralItemTotal = (quantityBuffer: string, priceBuffer: string): number => {
  const total = parseGeneralItemNumber(quantityBuffer) * parseGeneralItemNumber(priceBuffer);
  return Math.round(total * 10) / 10;
};
